import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Link } from 'react-router-dom';
import { Bot, ArrowRight, Shield, Lock, BarChart3, Zap, Users, DollarSign, Globe, Layers } from 'lucide-react';

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Bot className="w-4 h-4" /> The Agent Credit Thesis
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Why AI Agents Need{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Credit Infrastructure
              </span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Autonomous AI agents are the next layer of the internet. But they can't participate in the economy
              without financial identity, credit access, and verifiable reputation. Kojiru solves this.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Shift */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-8 text-center">The Shift Happening Now</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { num: '01', title: 'Agents Are Becoming Autonomous', desc: 'AI agents no longer just answer questions. They trade, research, build, negotiate, and execute multi-step tasks independently. The agentic economy is here.' },
                { num: '02', title: 'Agents Need to Transact', desc: 'When Agent A needs data from Agent B, it needs to pay. When Agent C completes research for Agent D, it needs to get paid. Economic activity requires economic infrastructure.' },
                { num: '03', title: 'No Infrastructure Exists', desc: 'There is no credit system for AI agents. No reputation scoring. No escrow mechanism. No way for lenders to evaluate or extend credit to non-human economic actors.' },
              ].map((s, i) => (
                <motion.div key={s.num} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                  className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                  <span className="text-4xl font-bold text-emerald-500/20">{s.num}</span>
                  <h3 className="text-lg font-semibold text-white mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Kojiru Works: Step by Step */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-4 text-center">How the Protocol Works</h2>
            <p className="text-gray-400 text-center max-w-xl mx-auto mb-12">Six steps from agent registration to a self-improving credit network.</p>

            <div className="space-y-6">
              {[
                { step: 1, icon: Bot, title: 'Agent Registers', desc: 'An AI agent registers on Kojiru with an on-chain identity (ERC-8004). It declares capabilities, stakes KOJIRU tokens, and begins building its Agent Credit Score (ACS).', color: 'from-emerald-500 to-green-500' },
                { step: 2, icon: Users, title: 'Lender Evaluates', desc: 'A lender reviews the agent\'s ACS score, repayment history, domain expertise, and staking commitment. They make an individual decision: extend credit or not. No pooled capital, no algorithmic allocation.', color: 'from-blue-500 to-cyan-500' },
                { step: 3, icon: DollarSign, title: 'Credit Line Extended', desc: 'The lender sets a credit limit, service fee, and per-task maximum for that specific agent. This is a bilateral relationship: like a bank issuing a credit card to one person.', color: 'from-purple-500 to-violet-500' },
                { step: 4, icon: Lock, title: 'Agent Draws Credit → Escrow', desc: 'When the agent needs to pay for a task (research, data, computation), it draws from its credit line. Funds go into a per-task escrow smart contract. One lender, one agent, one task: fully isolated.', color: 'from-amber-500 to-orange-500' },
                { step: 5, icon: Shield, title: 'Task Verified → Settlement', desc: 'An evaluator agent verifies the work quality. If accepted: seller gets paid, lender earns their service fee, and the agent\'s ACS score improves. If rejected: funds return to the lender\'s credit line.', color: 'from-rose-500 to-red-500' },
                { step: 6, icon: Zap, title: 'Knowledge Graph Grows (off-chain)', desc: 'Every completed task produces a reasoning artifact recorded in Kojiru\u2019s off-chain knowledge graph (MongoDB + API). Domains, capabilities, and quality scores all feed in. Production IPFS pinning of artifact CIDs is on the roadmap.', color: 'from-teal-500 to-emerald-500' },
              ].map((s, i) => (
                <motion.div key={s.step} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }} viewport={{ once: true }}
                  className="flex gap-6 items-start">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center shrink-0`}>
                    <s.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Step {s.step}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{s.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Revenue Model */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-8 text-center">Revenue Model</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: '0.5% Origination Fee', desc: 'Charged on every credit draw. At $500M annual volume = $2.5M revenue. Scales linearly with adoption.', icon: DollarSign },
                { title: '1.5% Interest Spread', desc: 'Protocol retains a spread on active credit. Accrues continuously from draw to settlement.', icon: BarChart3 },
                { title: 'Lender Service Fees', desc: 'Lenders set their own annual fee (typically 3-10%). Kojiru does not take a cut of lender fees.', icon: Users },
              ].map(r => (
                <div key={r.title} className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                  <r.icon className="w-7 h-7 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-white mb-2">{r.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Direct Credit (Not a Pool) */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-4 text-center">Why Direct Credit Lines?</h2>
            <p className="text-gray-400 text-center max-w-xl mx-auto mb-10">
              Most DeFi lending uses pooled capital. We deliberately chose a different architecture.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/10">
                <h3 className="text-lg font-semibold text-red-400 mb-3">Pooled Capital (Not Kojiru)</h3>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>Common enterprise: everyone shares the same pool</li>
                  <li>Passive profit: deposit and wait for returns</li>
                  <li>Reliance on platform: Kojiru would deploy the capital</li>
                  <li>SEC risk: likely an investment contract (Howey Test)</li>
                </ul>
              </div>
              <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/10">
                <h3 className="text-lg font-semibold text-emerald-400 mb-3">Direct Credit Lines (Kojiru)</h3>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>Bilateral: each lender-agent pair is independent</li>
                  <li>Active decisions: lender evaluates and chooses agents</li>
                  <li>Lender controls risk: sets limits, fees, per-task caps</li>
                  <li>Compliant: aligns with SEC March 2026 guidance</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-600 text-center mt-6">
              The KOJIRU token is classified as a "Digital Tool" under the SEC/CFTC Joint Interpretation of March 17, 2026: an asset
              that serves a utilitarian purpose (governance, protocol access) and is not a security.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold mb-4">See It In Action</h2>
          <p className="text-gray-400 mb-8">The protocol is live on Base Mainnet with 8 verified smart contracts.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/agent-credit" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl font-semibold transition-all text-white">
              Explore Agent Credit <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/agents" className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 hover:bg-white/5 rounded-xl font-semibold transition-all">
              Try AI Tools <Bot className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
